import {useCallback, useEffect, useState} from 'react';
import {unstable_batchedUpdates} from 'react-dom';

// One ResizeObserver serves every cell using this hook, routing each entry to its element's callback, so a
// long list does not create an observer per cell. It is created on the first observe and disconnected when
// the last element leaves, so nothing outlives the cells that use it.
//
// Cells are measured on the next animation frame, not inside the observer's callback. A cell that turns
// truncated re-renders (it gains a tooltip), and a layout change made inside the callback would be reported
// again in the same frame, which the browser flags as "ResizeObserver loop completed with undelivered
// notifications". A cell that leaves before the frame is skipped, since its callback is gone by then.
const callbacks = new Map<Element, () => void>();
let sharedObserver: ResizeObserver | null = null;

function observe(element: Element, callback: () => void): void {
	if (sharedObserver === null) {
		sharedObserver = new ResizeObserver(entries => {
			const targets = entries.map(entry => entry.target);
			// One render for every cell measured in this frame, not one per cell.
			window.requestAnimationFrame(() =>
				unstable_batchedUpdates(() => targets.forEach(target => callbacks.get(target)?.()))
			);
		});
	}
	callbacks.set(element, callback);
	sharedObserver.observe(element);
}

function unobserve(element: Element): void {
	callbacks.delete(element);
	if (sharedObserver === null) return;
	sharedObserver.unobserve(element);
	if (callbacks.size === 0) {
		sharedObserver.disconnect();
		sharedObserver = null;
	}
}

// Measures whenever the element resizes - dragging a column, hiding or showing one, grouping, and window
// resizes that change the cell's width - through the shared ResizeObserver, which also reports each element
// once when observation starts. Layout is never read during render. Where ResizeObserver does not exist
// (jsdom), it measures once after mount. Returns a callback ref, the latest measure, and a function that measures
// again at once, for a caller that has just changed the element's size itself.
function useMeasured<T extends HTMLElement, R>(read: (element: T) => R, initial: R): [(node: T | null) => void, R, () => void] {
	const [element, setElement] = useState<T | null>(null);
	const [value, setValue] = useState<R>(initial);
	const measureNow = useCallback(() => {
		if (element !== null) setValue(read(element));
		// read is one of the module-level measures below, so it never changes between renders.
	}, [element]);

	useEffect(() => {
		if (element === null) return;
		const measure = () => setValue(read(element));
		if (typeof ResizeObserver === 'undefined') {
			measure();
			return;
		}
		observe(element, measure);
		return () => unobserve(element);
		// read is one of the module-level measures below, so it never changes between renders.
	}, [element]);

	return [setElement, value, measureNow];
}

function useIsTruncated<T extends HTMLElement>(isCutOff: (element: T) => boolean): [(node: T | null) => void, boolean] {
	const [ref, isTruncated] = useMeasured<T, boolean>(isCutOff, false);
	return [ref, isTruncated];
}

const isWiderThanBox = (element: HTMLElement): boolean => element.scrollWidth > element.clientWidth;
// A clamped box that shows every line still reports a scrollHeight a couple of pixels over its clientHeight
// (glyph overhang and line-height rounding: a 16px one-line label measures 18px). A cut-off name hides a
// whole line, so only overflow of more than half a line counts.
const isTallerThanBox = (element: HTMLElement): boolean => {
	const style = window.getComputedStyle(element);
	const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2 || 0;
	return element.scrollHeight - element.clientHeight > lineHeight / 2;
};

/** Detects whether a one-line (`white-space: nowrap; text-overflow: ellipsis`) element is cut off, by
 * comparing its full (scroll) width against its visible (client) width. A height check cannot see this:
 * nowrap text never wraps, so the box never grows taller than one line. */
export function useIsTruncatedWidth<T extends HTMLElement = HTMLElement>(): [(node: T | null) => void, boolean] {
	return useIsTruncated<T>(isWiderThanBox);
}

/** Detects whether a line-clamped (`-webkit-line-clamp`) element is cut off, by comparing its full (scroll)
 * height against its visible (client) height. Clamped text wraps before it clips, so it only ever
 * overflows downwards. */
export function useIsTruncatedHeight<T extends HTMLElement = HTMLElement>(): [(node: T | null) => void, boolean] {
	return useIsTruncated<T>(isTallerThanBox);
}

// A box's rendered width in CSS pixels, fractions kept, or null while it has none (not laid out, or hidden).
const laidOutWidth = (element: HTMLElement): number | null => {
	const {width} = element.getBoundingClientRect();
	return width > 0 ? width : null;
};

/** The element's rendered width, read again whenever it resizes, or null until it has one. Measured through
 * the same shared ResizeObserver as the truncation hooks, so a list of cells still has one observer. The third
 * item reads the width again at once: called from a layout effect after the caller changed the element's size,
 * it brings the width up to date before paint rather than on the observer's next frame. */
export function useElementWidth<T extends HTMLElement = HTMLElement>(): [(node: T | null) => void, number | null, () => void] {
	return useMeasured<T, number | null>(laidOutWidth, null);
}
